"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { LinkedInIcon } from "@/components/icons/BrandIcons";
import { TeamMember } from "@/cms/team";

interface DynamicTeamProps {
	members: TeamMember[];
	className?: string;
	showTitle?: boolean;
	title?: string;
}

export function DynamicTeam({ 
	members, 
	className = "", 
	showTitle = true,
	title = "Meet the Team"
}: DynamicTeamProps) {
	return (
		<section className={`py-20 ${className}`}>
			<div className="max-w-7xl mx-auto">
				{/* Header */}
				{showTitle && (
					<div className="mb-16">
						<h2 className="text-3xl font-bold text-gray-900 md:text-4xl">
							{title}
						</h2>
					</div>
				)}
				
				{/* Team members grid - 3 in a row */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-24">
					{members.map((member, index) => (
						<div 
							key={index} 
							className="bg-white h-full flex flex-col"
						>
							{/* Image with bg-ss-lightest-blue background */}
							<div className="relative h-64 w-full overflow-hidden rounded-3xl bg-ss-lightest-blue flex items-center justify-center">
								<div className="relative w-48 h-48 overflow-hidden rounded-2xl">
									<Image
										src={member.image}
										alt={`${member.name}'s Profile Picture`}
										fill
										className="object-cover"
										style={{ 
											objectPosition: "center center"
										}}
									/>
								</div>
							</div>
							
							{/* Content */}
							<div className="pt-4 flex-1 flex flex-col">
								{/* Name and LinkedIn */}
								<div className="flex items-center justify-start mb-1">
									<h5 className="text-xl font-semibold text-gray-900 mr-2">
										{member.name}
									</h5>
									<Link 
										href={member.linkedin} 
										target="_blank"
										className="text-xl text-gray-700 transition duration-150 ease-in-out hover:text-ss-blue focus:outline-hidden focus:ring-2 focus:ring-ss-blue focus:ring-offset-2"
										aria-label={`Visit ${member.name}'s LinkedIn profile`}
									>
										<LinkedInIcon aria-hidden="true" />
									</Link>
								</div>
								
								{/* Role */}
								<p className="text-sm font-medium text-gray-600 mb-3">
									{member.role}
								</p>
								
								{/* Description */}
								<p className="text-sm leading-relaxed text-gray-700 flex-1">
									{member.description}
								</p>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
